import clsx from 'clsx';
import * as React from 'react';
import ResizeObserver from 'resize-observer-polyfill';
import {useLocation} from 'react-router-dom';

import {layoutManager} from 'common/report/util/layout-manager';
import {isTestEnv} from 'common/report/util/env';
import {ReportMessageBannerReact} from 'common/widgets/report-message-banner';
import {ReportBannersReact} from 'common/widgets/report-banners';
import {getDownloadModeFromLocation} from 'common/report/util';
import {DownloadMode} from 'common/report/constants';

import styles from './index.module.styl';

// Note: CSS padding percent is based on the parent's width, which is .width-frame's width in this case
export const HORIZONTAL_CONTENT_PADDING_STYLE = `calc(min(96px, 5.5%))`;

type ContentLayoutProps = React.PropsWithChildren<{
  className?: string;
  revealedBannerIdentifiers?: Set<string>;
  onContentLayoutResized?: (_: {padding: number}) => void;
}>;

/**
 * ContentLayout contains the page content (which will be scrollable). It also adds banners to the top of this
 * scrollable area. Currently, many/all the simple layout reports, which don't have the sidenav, use the deprecated
 * SidenavLayout. They should all be ported to use this component.
 * Some more details here: https://github.com/mixpanel/analytics/pull/62684#discussion_r1600639669
 */
export const ContentLayout: React.FC<ContentLayoutProps> = (props: ContentLayoutProps) => {
  const location = useLocation();
  const downloadMode = getDownloadModeFromLocation(location);

  if (downloadMode !== DownloadMode.None) {
    return <div className={props.className}>{props.children}</div>;
  }

  const paddingFrameRef = React.useRef<HTMLDivElement>();
  React.useEffect(() => {
    let paddingFrameResizeObserver: ResizeObserver;
    if (paddingFrameRef.current) {
      layoutManager.setConfig({reportScrollContainer: paddingFrameRef.current});
      if (!paddingFrameResizeObserver) {
        paddingFrameResizeObserver = new ResizeObserver((entries: ResizeObserverEntry[]) => {
          const padding = parseFloat(
            // NOTE: This ResizeObserver listener is called when paddingFrame is unmounted, with paddingFrame having
            // width/height of 0. paddingFrameRef.current will be null at this point. Hence, we use
            // entries[0].target instead of paddingFrameRef.current.
            window.getComputedStyle(entries[0].target, null).getPropertyValue(`padding-left`),
          );
          props.onContentLayoutResized?.({padding});
        });
        paddingFrameResizeObserver.observe(paddingFrameRef.current);
      }
    }

    return () => {
      paddingFrameResizeObserver?.disconnect();
    };
  }, [paddingFrameRef.current]);

  return (
    <div className={clsx(props.className, `inherit-size`)}>
      <div
        className={clsx(`inherit-size`)}
        style={{
          margin: `0 auto`,
        }}
      >
        <div
          className={clsx(styles.paddingFrame, `inherit-size`)}
          style={{
            boxSizing: `border-box`,
            padding: `0 ${HORIZONTAL_CONTENT_PADDING_STYLE} 6vh ${HORIZONTAL_CONTENT_PADDING_STYLE}`,
          }}
          ref={paddingFrameRef}
        >
          {!isTestEnv() && (
            // only show banners if it's not test env, since some test suites aren't setup to mock banner
            // API requests
            <div className={clsx(styles.bannerContainer)}>
              <ReportMessageBannerReact />
              <ReportBannersReact revealedBannerIdentifiers={props.revealedBannerIdentifiers} />
            </div>
          )}
          {props.children}
        </div>
      </div>
    </div>
  );
};
